/**
 * Attaches `.meta({ id })` to the OpenAPI-relevant schema variants produced by
 * `getZodSchemaFromClass()`. This gives each variant a stable component identity
 * that `zod-to-openapi` uses to emit `$ref` instead of inlining schemas.
 *
 * Kept as a standalone function so it can be removed from `getZodSchemaFromClass`
 * without touching the core schema builder if the OpenAPI meta strategy changes.
 *
 * Variant mapping:
 * - `baseLax`  → id: `schemaId`            (canonical read/response component)
 * - `create`   → id: `${schemaId}Create`   (POST body component)
 * - `update`   → id: `${schemaId}Update`   (PATCH body component)
 * - `base` and `createLax` are left untagged — they are not OpenAPI components.
 */
export function augmentSchemasWithOpenApiMeta<
    S extends {
        baseLax: { meta(opts: { id: string }): any };
        create: { meta(opts: { id: string }): any };
        update: { meta(opts: { id: string }): any };
    },
>(cls: { schemaKey?: string; name?: string }, schemas: S): S {
    // schemaKey is a static override on SchemaBase subclasses; falls back to the JS class name.
    const schemaId = cls.schemaKey ?? cls.name;
    if (!schemaId) {
        throw new Error("Cannot derive OpenAPI schema ID: class has no name or schemaKey");
    }
    return {
        ...schemas,
        baseLax: schemas.baseLax.meta({ id: schemaId }),
        create: schemas.create.meta({ id: `${schemaId}Create` }),
        update: schemas.update.meta({ id: `${schemaId}Update` }),
    };
}
